import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import './GlobalScrollControls.css';
import { useI18n } from '../i18n';

const GlobalScrollControls = () => {
  const { t } = useI18n();
  const [canScrollTop, setCanScrollTop] = useState(false);
  const [canScrollBottom, setCanScrollBottom] = useState(true);
  const location = useLocation();

  // Busca el contenedor principal activo
  const getActiveContainer = useCallback(() => {
    // Si estamos en tareas, buscar su contenedor central
    if (location.pathname.includes('/tareas')) {
      const tareasMain = document.querySelector('.tareas-main');
      if (tareasMain) return tareasMain;
    }
    return document.documentElement;
  }, [location.pathname]);

  const evaluateScroll = (node) => {
    if (!node) return;
    const scrollY = node.scrollTop || window.scrollY;
    const clientHeight = node.clientHeight || window.innerHeight;
    const scrollHeight = node.scrollHeight || document.documentElement.scrollHeight;

    setCanScrollTop(scrollY > 20);
    // Margen de 20px para no quedar bloqueado por redondeos de píxeles
    setCanScrollBottom(scrollY + clientHeight < scrollHeight - 20);
  };

  useEffect(() => {
    const handleScroll = (e) => {
      const target = (e.target === document || !e.target) ? document.documentElement : e.target;

      // Ignorar eventos de scroll provenientes de paneles secundarios
      const className = target.className || '';
      if (typeof className === 'string' && (className.includes('historial') || className.includes('sidebar'))) {
        return;
      }

      evaluateScroll(target);
    };

    window.addEventListener('scroll', handleScroll, true);

    // Vigilante de tamaño del DOM para habilitar flechas cuando carga contenido asíncrono
    const resizeObserver = new ResizeObserver(() => {
      evaluateScroll(getActiveContainer());
    });

    resizeObserver.observe(document.body);
    const activeNode = getActiveContainer();
    if (activeNode && activeNode !== document.documentElement) {
      resizeObserver.observe(activeNode);
    }

    // Chequeo inicial
    evaluateScroll(getActiveContainer());

    return () => {
      window.removeEventListener('scroll', handleScroll, true);
      resizeObserver.disconnect();
    };
  }, [location.pathname, getActiveContainer]);

  const forceScroll = (direction) => {
    const behavior = 'smooth';
    const isTop = direction === 'top';

    if (isTop && !canScrollTop) return;
    if (!isTop && !canScrollBottom) return;

    // 1. Scroll a window global
    const targetY = isTop ? 0 : document.documentElement.scrollHeight;
    window.scrollTo({ top: targetY, behavior });

    // 2. Scroll a contenedores internos (excluyendo sidebars)
    const containers = [
      document.querySelector('.tareas-main'),
      document.querySelector('.task-recipe-view'),
      document.querySelector('.app-container')
    ];

    containers.forEach(container => {
      if (container && !container.className.includes('historial') && !container.className.includes('sidebar')) {
        const y = isTop ? 0 : container.scrollHeight;
        container.scrollTo({ top: y, behavior });
      }
    });
  };

  return (
    <div className="scroll-oval-hit-area">
      <div className="scroll-oval-container">
        <button
          className={`oval-arrow ${!canScrollTop ? 'disabled' : ''}`}
          onClick={() => forceScroll('top')}
          title={t('globalScroll.top')}
          disabled={!canScrollTop}
        >
          ▲
        </button>
        <div className="scroll-anchor-line" />
        <button
          className={`oval-arrow ${!canScrollBottom ? 'disabled' : ''}`}
          onClick={() => forceScroll('bottom')}
          title={t('globalScroll.bottom')}
          disabled={!canScrollBottom}
        >
          ▼
        </button>
      </div>
    </div>
  );
};

export default GlobalScrollControls;
